import { DEFAULT_THEME } from "./theme.js";
import { WaveformGenerator } from "../waveform/WaveformGenerator.js";

export class ViewportRenderer {
  constructor(canvasElement, timeline, daw, theme = {}) {
    this.canvas = canvasElement;
    this.ctx = this.canvas.getContext("2d");
    this.timeline = timeline;
    this.daw = daw;
    this.renderScheduled = false;
    this.minTrackHeight = 80;
    this.trackSpacing = 10;
    this.bottomPadding = 40;
    this.rulerHeight = 60;
    this.verticalScrollOffset = 0;
    this.scrollbarDragState = null;
    this.horizontalScrollbarDragState = null;

    this.minimapCanvas = null;
    this.minimapCtx = null;
    this.minimapDragState = null;

    // Selection drag state
    this.selectionDragState = null; // { type: 'create' | 'move' | 'resize-start' | 'resize-end', startTime, startMouseTime }
    this.regionDragState = null; // { type: 'move' | 'resize-start' | 'resize-end', regionIndex, originalRegion, startMouseTime }
    this.hoveredRegionIndex = null; // Index of region being hovered over

    // Throttle for region change callbacks during drag
    this.lastRegionChangeCallbackTime = 0;
    this.regionChangeCallbackThrottleMs = 16; // ~60fps

    // Track mipmap levels for logging changes
    this.lastMipmapLevels = new Map(); // trackId -> resolution

    // Theme configuration (merge with defaults)
    this.theme = { ...DEFAULT_THEME, ...theme };

    // Ensure scrollbar config exists and merge legacy properties
    if (!this.theme.scrollbar) {
      this.theme.scrollbar = DEFAULT_THEME.scrollbar;
    }
    this.theme.scrollbar = {
      ...DEFAULT_THEME.scrollbar,
      ...this.theme.scrollbar,
      // Support legacy properties
      width: this.theme.scrollbar.width || this.theme.scrollbarWidth,
      trackColor:
        this.theme.scrollbar.trackColor || this.theme.scrollbarTrackColor,
      thumbColor:
        this.theme.scrollbar.thumbColor || this.theme.scrollbarThumbColor,
      thumbColorActive:
        this.theme.scrollbar.thumbColorActive ||
        this.theme.scrollbarThumbColorActive,
      thumbBorder:
        this.theme.scrollbar.thumbBorder || this.theme.scrollbarThumbBorder,
    };

    // Keep legacy property for backward compatibility
    this.scrollbarWidth = this.theme.scrollbar.width;

    this.setupCanvas();
    this.setupEventListeners();
  }

  /**
   * Update theme and re-render
   */
  setTheme(newTheme) {
    this.theme = { ...DEFAULT_THEME, ...newTheme };

    // Ensure scrollbar config exists and merge legacy properties
    if (!this.theme.scrollbar) {
      this.theme.scrollbar = DEFAULT_THEME.scrollbar;
    }
    this.theme.scrollbar = {
      ...DEFAULT_THEME.scrollbar,
      ...this.theme.scrollbar,
      // Support legacy properties
      width: this.theme.scrollbar.width || this.theme.scrollbarWidth,
      trackColor:
        this.theme.scrollbar.trackColor || this.theme.scrollbarTrackColor,
      thumbColor:
        this.theme.scrollbar.thumbColor || this.theme.scrollbarThumbColor,
      thumbColorActive:
        this.theme.scrollbar.thumbColorActive ||
        this.theme.scrollbarThumbColorActive,
      thumbBorder:
        this.theme.scrollbar.thumbBorder || this.theme.scrollbarThumbBorder,
    };

    // Keep legacy property for backward compatibility
    this.scrollbarWidth = this.theme.scrollbar.width;
    this.scheduleRender();
  }

  setMinimapCanvas(minimapCanvas) {
    this.minimapCanvas = minimapCanvas;
    this.minimapCtx = minimapCanvas ? minimapCanvas.getContext("2d") : null;
    if (this.minimapCanvas) {
      this.setupMinimapEventListeners();
      // Trigger resize to properly size the minimap canvas
      this.resizeCanvas();
    }
  }

  setupCanvas() {
    this.resizeCanvas();
    window.addEventListener("resize", () => this.resizeCanvas());
  }

  resizeCanvas() {
    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = this.canvas.offsetWidth * dpr;
    this.canvas.height = this.canvas.offsetHeight * dpr;
    this.ctx.scale(dpr, dpr);

    if (this.minimapCanvas) {
      this.minimapCanvas.width = this.minimapCanvas.offsetWidth * dpr;
      this.minimapCanvas.height = this.minimapCanvas.offsetHeight * dpr;
      this.minimapCtx.scale(dpr, dpr);
    }

    this.timeline.updateViewport(this.canvas.offsetWidth);
    this.scheduleRender();
  }

  calculateTrackHeight() {
    const numTracks = this.timeline.tracks.length;
    if (numTracks === 0) return this.minTrackHeight;

    const topPadding = 20;
    const bottomPadding = 20;
    const availableHeight =
      this.canvas.offsetHeight - this.rulerHeight - topPadding - bottomPadding;
    const totalSpacing = (numTracks - 1) * this.trackSpacing;
    const heightPerTrack = (availableHeight - totalSpacing) / numTracks;

    return Math.max(this.minTrackHeight, heightPerTrack);
  }

  getTotalContentHeight() {
    const numTracks = this.timeline.tracks.length;
    if (numTracks === 0) return 0;

    const topPadding = 20;
    const bottomPadding = 20; // Match top padding for symmetry
    const trackHeight = this.calculateTrackHeight();

    // Total height = top padding + all tracks + spacing between tracks + bottom padding
    return (
      topPadding +
      numTracks * trackHeight +
      (numTracks - 1) * this.trackSpacing +
      bottomPadding
    );
  }

  getMaxVerticalScroll() {
    const contentHeight = this.getTotalContentHeight();
    const viewportHeight = this.canvas.offsetHeight - this.rulerHeight;
    return Math.max(0, contentHeight - viewportHeight);
  }

  setupEventListeners() {
    this.canvas.addEventListener("wheel", (e) => this.handleWheel(e), {
      passive: false,
    });
    this.canvas.addEventListener("mousedown", (e) => this.handleMouseDown(e));
    this.canvas.addEventListener("mousemove", (e) => this.handleMouseMove(e));
    this.canvas.addEventListener("mouseup", (e) => this.handleMouseUp(e));
    this.canvas.addEventListener("dblclick", (e) => this.handleDoubleClick(e));
    this.canvas.addEventListener("contextmenu", (e) =>
      this.handleContextMenu(e)
    );

    // Keyboard events for selection
    window.addEventListener("keydown", (e) => this.handleKeyDown(e));
  }

  setupMinimapEventListeners() {
    this.minimapCanvas.addEventListener("mousedown", (e) =>
      this.handleMinimapMouseDown(e)
    );
    this.minimapCanvas.addEventListener("mousemove", (e) =>
      this.handleMinimapMouseMove(e)
    );
    this.minimapCanvas.addEventListener("mouseup", (e) =>
      this.handleMinimapMouseUp(e)
    );
    this.minimapCanvas.addEventListener("mouseleave", () => {
      if (this.minimapDragState) {
        this.minimapDragState = null;
        this.minimapCanvas.style.cursor = "pointer";
      }
    });
  }

  handleWheel(e) {
    e.preventDefault();

    if (e.shiftKey) {
      const scrollAmount = e.deltaY;
      this.verticalScrollOffset += scrollAmount;
      const maxScroll = this.getMaxVerticalScroll();
      this.verticalScrollOffset = Math.max(
        0,
        Math.min(maxScroll, this.verticalScrollOffset)
      );
      this.scheduleRender();
    } else if (e.ctrlKey) {
      this.zoomAtPoint(e.offsetX, e.deltaY > 0 ? 0.9 : 1.1);
    } else {
      const scrollAmount = e.deltaY * 0.01;
      this.timeline.scroll(scrollAmount, this.canvas.offsetWidth);
      this.timeline.updateViewport(this.canvas.offsetWidth);
      this.scheduleRender();
    }
  }

  zoomAtPoint(canvasX, zoomFactor) {
    const timeAtCursor = this.timeline.canvasXToTime(canvasX);

    this.timeline.pixelsPerSecond *= zoomFactor;
    this.timeline.pixelsPerSecond = Math.min(
      500,
      Math.max(0.1, this.timeline.pixelsPerSecond)
    );

    const newCanvasWidth = this.canvas.offsetWidth;
    this.timeline.updateViewport(newCanvasWidth);

    const newTimeAtCursor = this.timeline.canvasXToTime(canvasX);
    this.timeline.viewportScrollTime += timeAtCursor - newTimeAtCursor;

    const effectiveDuration = this.timeline.getEffectiveDuration(
      this.canvas.offsetWidth
    );
    const maxScroll = Math.max(
      0,
      effectiveDuration - this.timeline.getVisibleDuration()
    );
    this.timeline.viewportScrollTime = Math.max(
      0,
      Math.min(maxScroll, this.timeline.viewportScrollTime)
    );

    this.timeline.updateViewport(newCanvasWidth);
    this.scheduleRender();

    if (this.daw && this.daw.onZoomChange) {
      this.daw.onZoomChange(this.timeline.pixelsPerSecond);
    }

    if (this.daw && this.daw.onScrollChange) {
      this.daw.onScrollChange(this.timeline.viewportScrollTime);
    }
  }

  getTrackAtPoint(canvasX, canvasY) {
    const time = this.timeline.canvasXToTime(canvasX);
    const trackHeight = this.calculateTrackHeight();
    const scrolledY = canvasY + this.verticalScrollOffset;

    for (let i = 0; i < this.timeline.tracks.length; i++) {
      const track = this.timeline.tracks[i];
      const trackY = 20 + i * (trackHeight + this.trackSpacing);

      if (scrolledY >= trackY && scrolledY <= trackY + trackHeight) {
        const trackStartTime = track.timeOffset;
        const trackEndTime =
          track.timeOffset +
          (track.audioBuffer ? track.audioBuffer.duration : 0);

        if (time >= trackStartTime && time <= trackEndTime) {
          return { track, index: i, trackY };
        }
      }
    }

    return null;
  }

  getTimeAtPoint(canvasX) {
    return this.timeline.canvasXToTime(canvasX);
  }

  getSelectionHandleAtPoint(canvasX, canvasY) {
    if (!this.timeline.selection) return null;

    const rulerY = this.canvas.offsetHeight - this.rulerHeight;
    if (canvasY < rulerY) return null; // Only detect handles in ruler area

    const startX = this.timeline.timeToCanvasX(this.timeline.selection.start);
    const endX = this.timeline.timeToCanvasX(this.timeline.selection.end);
    const handleWidth = this.theme.selectionHandleWidth;

    if (Math.abs(canvasX - startX) <= handleWidth / 2) {
      return "start";
    }
    if (Math.abs(canvasX - endX) <= handleWidth / 2) {
      return "end";
    }

    return null;
  }

  isPointInSelection(canvasX, canvasY) {
    if (!this.timeline.selection) return false;

    const rulerY = this.canvas.offsetHeight - this.rulerHeight;
    if (canvasY < rulerY) return false; // Only detect in ruler area

    const startX = this.timeline.timeToCanvasX(this.timeline.selection.start);
    const endX = this.timeline.timeToCanvasX(this.timeline.selection.end);

    return canvasX >= startX && canvasX <= endX;
  }

  // Region detection methods
  getRegionAtPoint(canvasX, canvasY) {
    const rulerY = this.canvas.offsetHeight - this.rulerHeight;
    if (canvasY < rulerY) return null; // Only detect regions in ruler area

    const regions = this.timeline.getActiveRegions();
    const mouseTime = this.timeline.canvasXToTime(canvasX);

    for (let i = 0; i < regions.length; i++) {
      const region = regions[i];
      if (mouseTime >= region.start && mouseTime <= region.end) {
        return { region, index: i };
      }
    }

    return null;
  }

  getRegionAtPointAnywhere(canvasX, canvasY) {
    // Check for region at any Y position (not just ruler)
    const regions = this.timeline.getActiveRegions();
    const mouseTime = this.timeline.canvasXToTime(canvasX);

    for (let i = 0; i < regions.length; i++) {
      const region = regions[i];
      if (mouseTime >= region.start && mouseTime <= region.end) {
        return { region, index: i };
      }
    }

    return null;
  }

  getRegionHandleAtPoint(canvasX, canvasY) {
    const rulerY = this.canvas.offsetHeight - this.rulerHeight;
    if (canvasY < rulerY) return null; // Only detect handles in ruler area

    const regions = this.timeline.getActiveRegions();
    const handleWidth = 8; // Handle detection width in pixels

    for (let i = 0; i < regions.length; i++) {
      const region = regions[i];
      const startX = this.timeline.timeToCanvasX(region.start);
      const endX = this.timeline.timeToCanvasX(region.end);

      if (Math.abs(canvasX - startX) <= handleWidth) {
        return { type: "start", index: i, region };
      }
      if (Math.abs(canvasX - endX) <= handleWidth) {
        return { type: "end", index: i, region };
      }
    }

    return null;
  }

  handleMouseDown(e) {
    // Check vertical scrollbar
    if (this.isPointOnVerticalScrollbarThumb(e.offsetX, e.offsetY)) {
      this.scrollbarDragState = {
        isDragging: true,
        startMouseY: e.offsetY,
        startScrollOffset: this.verticalScrollOffset,
      };
      this.canvas.style.cursor = "grabbing";
      this.scheduleRender();
      return;
    }

    if (this.isPointOnVerticalScrollbarTrack(e.offsetX, e.offsetY)) {
      const geom = this.getVerticalScrollbarGeometry();
      if (e.offsetY < geom.thumbY) {
        const pageSize = this.canvas.offsetHeight - this.rulerHeight;
        this.verticalScrollOffset = Math.max(
          0,
          this.verticalScrollOffset - pageSize
        );
      } else if (e.offsetY > geom.thumbY + geom.thumbHeight) {
        const pageSize = this.canvas.offsetHeight - this.rulerHeight;
        this.verticalScrollOffset = Math.min(
          geom.maxScroll,
          this.verticalScrollOffset + pageSize
        );
      }
      this.scheduleRender();
      return;
    }

    // Check horizontal scrollbar
    if (this.isPointOnHorizontalScrollbarThumb(e.offsetX, e.offsetY)) {
      this.horizontalScrollbarDragState = {
        isDragging: true,
        startMouseX: e.offsetX,
        startScrollTime: this.timeline.viewportScrollTime,
      };
      this.canvas.style.cursor = "grabbing";
      this.scheduleRender();
      return;
    }

    if (this.isPointOnHorizontalScrollbarTrack(e.offsetX, e.offsetY)) {
      const geom = this.getHorizontalScrollbarGeometry();
      if (e.offsetX < geom.thumbX) {
        // Click before thumb - page left
        const pageSize = this.timeline.getVisibleDuration();
        this.timeline.viewportScrollTime = Math.max(
          0,
          this.timeline.viewportScrollTime - pageSize
        );
        this.timeline.updateViewport(this.canvas.offsetWidth);
      } else if (e.offsetX > geom.thumbX + geom.thumbWidth) {
        // Click after thumb - page right
        const pageSize = this.timeline.getVisibleDuration();
        this.timeline.viewportScrollTime = Math.min(
          geom.maxScroll,
          this.timeline.viewportScrollTime + pageSize
        );
        this.timeline.updateViewport(this.canvas.offsetWidth);
      }
      this.scheduleRender();
      return;
    }

    const rulerY = this.canvas.offsetHeight - this.rulerHeight;
    const isInRuler = e.offsetY >= rulerY;

    // Check for interactions in ruler area
    if (isInRuler) {
      // Only allow left mouse button (button 0) for region interactions
      if (e.button !== 0) return;

      // Check if active region group is read-only
      const activeGroup = this.timeline.activeRegionGroupId
        ? this.timeline.regionGroups.get(this.timeline.activeRegionGroupId)
        : null;
      const isReadOnly = activeGroup && activeGroup.readOnly;

      // Check for region handle interaction first (existing regions)
      const regionHandle = this.getRegionHandleAtPoint(e.offsetX, e.offsetY);
      if (regionHandle) {
        if (!isReadOnly) {
          this.regionDragState = {
            type: regionHandle.type === "start" ? "resize-start" : "resize-end",
            regionIndex: regionHandle.index,
            originalRegion: { ...regionHandle.region },
            startMouseTime: this.getTimeAtPoint(e.offsetX),
          };
          this.canvas.style.cursor = "ew-resize";
        }
        return;
      }

      // Check if clicking in existing region to move it
      const regionAtPoint = this.getRegionAtPoint(e.offsetX, e.offsetY);
      if (regionAtPoint) {
        if (!isReadOnly) {
          this.regionDragState = {
            type: "move",
            regionIndex: regionAtPoint.index,
            originalRegion: { ...regionAtPoint.region },
            startMouseTime: this.getTimeAtPoint(e.offsetX),
          };
          this.canvas.style.cursor = "grab";
        }
        return;
      }

      // Check for temporary selection handle interaction (while creating)
      const handle = this.getSelectionHandleAtPoint(e.offsetX, e.offsetY);
      if (handle) {
        this.selectionDragState = {
          type: handle === "start" ? "resize-start" : "resize-end",
          startTime: this.timeline.selection[handle],
          startMouseTime: this.getTimeAtPoint(e.offsetX),
        };
        this.canvas.style.cursor = "ew-resize";
        return;
      }

      // Check if clicking in existing temporary selection to move it
      if (this.isPointInSelection(e.offsetX, e.offsetY)) {
        this.selectionDragState = {
          type: "move",
          startSelection: { ...this.timeline.selection },
          startMouseTime: this.getTimeAtPoint(e.offsetX),
        };
        this.canvas.style.cursor = "grabbing";
        return;
      }

      // Start creating new selection/region in ruler area (if not read-only and not at max)
      if (!isReadOnly) {
        // Check if we've reached the maxRegions limit
        if (
          activeGroup &&
          activeGroup.maxRegions !== null &&
          activeGroup.regions.length >= activeGroup.maxRegions
        ) {
          this.log(
            `Cannot create region: maximum of ${activeGroup.maxRegions} region(s) reached`
          );
          return;
        }

        const clickTime = this.getTimeAtPoint(e.offsetX);
        this.selectionDragState = {
          type: "create",
          startTime: clickTime,
          startMouseTime: clickTime,
        };
        const clickSample = this.timeline.timeToSamples(clickTime);
        this.timeline.selection = {
          start: clickTime,
          end: clickTime,
          startSample: clickSample,
          endSample: clickSample,
        };
        this.scheduleRender();
        if (this.daw && this.daw.onSelectionChange) {
          this.daw.onSelectionChange(this.timeline.selection);
        }
      }
      return;
    }

    // Track dragging (only with left mouse button)
    if (e.button !== 0) return;

    const trackInfo = this.getTrackAtPoint(e.offsetX, e.offsetY);

    if (trackInfo) {
      this.dragState = {
        track: trackInfo.track,
        startOffset: trackInfo.track.timeOffset,
        startMouseTime: this.getTimeAtPoint(e.offsetX),
        isDragging: true,
      };
      this.canvas.style.cursor = "grabbing";
    } else {
      this.dragState = {
        isDragging: false,
        clickTime: this.getTimeAtPoint(e.offsetX),
      };
    }
  }

  handleMouseMove(e) {
    // Handle vertical scrollbar dragging
    if (this.scrollbarDragState && this.scrollbarDragState.isDragging) {
      const deltaY = e.offsetY - this.scrollbarDragState.startMouseY;
      const geom = this.getVerticalScrollbarGeometry();

      const scrollableHeight = geom.scrollbarHeight - geom.thumbHeight;
      const scrollRatio = scrollableHeight > 0 ? deltaY / scrollableHeight : 0;
      const scrollDelta = scrollRatio * geom.maxScroll;

      this.verticalScrollOffset =
        this.scrollbarDragState.startScrollOffset + scrollDelta;
      this.verticalScrollOffset = Math.max(
        0,
        Math.min(geom.maxScroll, this.verticalScrollOffset)
      );

      this.scheduleRender();
      return;
    }

    // Handle horizontal scrollbar dragging
    if (
      this.horizontalScrollbarDragState &&
      this.horizontalScrollbarDragState.isDragging
    ) {
      const deltaX = e.offsetX - this.horizontalScrollbarDragState.startMouseX;
      const geom = this.getHorizontalScrollbarGeometry();

      const scrollableWidth = geom.scrollbarWidth - geom.thumbWidth;
      const scrollRatio = scrollableWidth > 0 ? deltaX / scrollableWidth : 0;
      const scrollDelta = scrollRatio * geom.maxScroll;

      this.timeline.viewportScrollTime =
        this.horizontalScrollbarDragState.startScrollTime + scrollDelta;
      this.timeline.viewportScrollTime = Math.max(
        0,
        Math.min(geom.maxScroll, this.timeline.viewportScrollTime)
      );
      this.timeline.updateViewport(this.canvas.offsetWidth);

      this.scheduleRender();
      return;
    }

    // Handle region dragging
    if (this.regionDragState) {
      const currentMouseTime = this.getTimeAtPoint(e.offsetX);
      const regions = this.timeline.getActiveRegions();

      if (this.regionDragState.regionIndex >= regions.length) {
        this.regionDragState = null;
        return;
      }

      const region = regions[this.regionDragState.regionIndex];
      let regionChanged = false;

      if (this.regionDragState.type === "resize-start") {
        const newStart = Math.max(0, currentMouseTime);
        if (newStart < region.end) {
          region.start = newStart;
          region.startSample = this.timeline.timeToSamples(newStart);
          regionChanged = true;
          this.scheduleRender();
        }
      } else if (this.regionDragState.type === "resize-end") {
        const newEnd = Math.max(0, currentMouseTime);
        if (newEnd > region.start) {
          region.end = newEnd;
          region.endSample = this.timeline.timeToSamples(newEnd);
          regionChanged = true;
          this.scheduleRender();
        }
      } else if (this.regionDragState.type === "move") {
        const delta = currentMouseTime - this.regionDragState.startMouseTime;
        const duration =
          this.regionDragState.originalRegion.end -
          this.regionDragState.originalRegion.start;
        let newStart = this.regionDragState.originalRegion.start + delta;
        newStart = Math.max(0, newStart);
        const newEnd = newStart + duration;
        region.start = newStart;
        region.end = newEnd;
        region.startSample = this.timeline.timeToSamples(newStart);
        region.endSample = this.timeline.timeToSamples(newEnd);
        regionChanged = true;
        this.scheduleRender();
      }

      // Fire throttled callback to update React state during drag
      if (regionChanged && this.daw && this.daw.onRegionsChange) {
        const now = Date.now();
        if (now - this.lastRegionChangeCallbackTime >= this.regionChangeCallbackThrottleMs) {
          this.daw.onRegionsChange(this.timeline.activeRegionGroupId);
          this.lastRegionChangeCallbackTime = now;
        }
      }

      return;
    }

    // Handle selection dragging
    if (this.selectionDragState) {
      const currentMouseTime = this.getTimeAtPoint(e.offsetX);

      if (this.selectionDragState.type === "create") {
        const start = Math.min(
          this.selectionDragState.startTime,
          currentMouseTime
        );
        const end = Math.max(
          this.selectionDragState.startTime,
          currentMouseTime
        );
        this.timeline.selection = {
          start,
          end,
          startSample: this.timeline.timeToSamples(start),
          endSample: this.timeline.timeToSamples(end),
        };
        this.scheduleRender();
        if (this.daw && this.daw.onSelectionChange) {
          this.daw.onSelectionChange(this.timeline.selection);
        }
      } else if (this.selectionDragState.type === "resize-start") {
        const newStart = Math.max(0, currentMouseTime);
        if (newStart < this.timeline.selection.end) {
          this.timeline.selection.start = newStart;
          this.timeline.selection.startSample =
            this.timeline.timeToSamples(newStart);
          this.scheduleRender();
          if (this.daw && this.daw.onSelectionChange) {
            this.daw.onSelectionChange(this.timeline.selection);
          }
        }
      } else if (this.selectionDragState.type === "resize-end") {
        const newEnd = Math.max(0, currentMouseTime);
        if (newEnd > this.timeline.selection.start) {
          this.timeline.selection.end = newEnd;
          this.timeline.selection.endSample =
            this.timeline.timeToSamples(newEnd);
          this.scheduleRender();
          if (this.daw && this.daw.onSelectionChange) {
            this.daw.onSelectionChange(this.timeline.selection);
          }
        }
      } else if (this.selectionDragState.type === "move") {
        const delta = currentMouseTime - this.selectionDragState.startMouseTime;
        const duration =
          this.selectionDragState.startSelection.end -
          this.selectionDragState.startSelection.start;
        let newStart = this.selectionDragState.startSelection.start + delta;
        newStart = Math.max(0, newStart);
        const newEnd = newStart + duration;
        this.timeline.selection = {
          start: newStart,
          end: newEnd,
          startSample: this.timeline.timeToSamples(newStart),
          endSample: this.timeline.timeToSamples(newEnd),
        };
        this.scheduleRender();
        if (this.daw && this.daw.onSelectionChange) {
          this.daw.onSelectionChange(this.timeline.selection);
        }
      }
      return;
    }

    if (this.dragState && this.dragState.isDragging) {
      const currentMouseTime = this.getTimeAtPoint(e.offsetX);
      const delta = currentMouseTime - this.dragState.startMouseTime;

      const newTimeOffset = Math.max(0, this.dragState.startOffset + delta);
      this.dragState.track.setTimeOffset(newTimeOffset);

      this.scheduleRender();
    } else {
      if (
        this.isPointOnVerticalScrollbarThumb(e.offsetX, e.offsetY) ||
        this.isPointOnHorizontalScrollbarThumb(e.offsetX, e.offsetY)
      ) {
        this.canvas.style.cursor = "grab";
      } else if (
        this.isPointOnVerticalScrollbarTrack(e.offsetX, e.offsetY) ||
        this.isPointOnHorizontalScrollbarTrack(e.offsetX, e.offsetY)
      ) {
        this.canvas.style.cursor = "pointer";
      } else {
        const rulerY = this.canvas.offsetHeight - this.rulerHeight;
        const isInRuler = e.offsetY >= rulerY;

        if (isInRuler) {
          // Check if active region group is read-only
          const activeGroup = this.timeline.activeRegionGroupId
            ? this.timeline.regionGroups.get(this.timeline.activeRegionGroupId)
            : null;
          const isReadOnly = activeGroup && activeGroup.readOnly;

          // Check for region handles first
          const regionHandle = this.getRegionHandleAtPoint(
            e.offsetX,
            e.offsetY
          );
          if (regionHandle) {
            this.canvas.style.cursor = isReadOnly ? "default" : "ew-resize";
            this.hoveredRegionIndex = regionHandle.index;
          } else {
            const regionAtPoint = this.getRegionAtPoint(e.offsetX, e.offsetY);
            if (regionAtPoint) {
              this.canvas.style.cursor = isReadOnly ? "default" : "grab";
              this.hoveredRegionIndex = regionAtPoint.index;
            } else {
              this.hoveredRegionIndex = null;
              // Check temporary selection handles
              const handle = this.getSelectionHandleAtPoint(
                e.offsetX,
                e.offsetY
              );
              if (handle) {
                this.canvas.style.cursor = "ew-resize";
              } else if (this.isPointInSelection(e.offsetX, e.offsetY)) {
                this.canvas.style.cursor = "grab";
              } else {
                this.canvas.style.cursor = isReadOnly ? "default" : "crosshair";
              }
            }
          }
          this.scheduleRender();
        } else {
          this.hoveredRegionIndex = null;
          const trackInfo = this.getTrackAtPoint(e.offsetX, e.offsetY);
          this.canvas.style.cursor = trackInfo ? "grab" : "crosshair";
        }
      }
    }
  }

  handleMouseUp(e) {
    if (this.scrollbarDragState) {
      this.scrollbarDragState = null;
      this.canvas.style.cursor = "default";
      this.scheduleRender();
      return;
    }

    if (this.horizontalScrollbarDragState) {
      this.horizontalScrollbarDragState = null;
      this.canvas.style.cursor = "default";
      this.scheduleRender();
      return;
    }

    // Complete region operation
    if (this.regionDragState) {
      // Merge overlapping regions after drag is complete
      this.timeline.mergeOverlappingRegions(this.timeline.activeRegionGroupId);

      this.regionDragState = null;
      this.canvas.style.cursor = "crosshair";
      this.scheduleRender();

      if (this.daw && this.daw.onRegionsChange) {
        this.daw.onRegionsChange(this.timeline.activeRegionGroupId);
      }
      return;
    }

    // Complete selection operation
    if (this.selectionDragState) {
      // If selection is too small (less than 0.01 seconds), clear it
      if (
        this.timeline.selection &&
        Math.abs(this.timeline.selection.end - this.timeline.selection.start) <
          0.01
      ) {
        this.timeline.selection = null;
        if (this.daw && this.daw.onSelectionChange) {
          this.daw.onSelectionChange(null);
        }
      } else if (this.timeline.selection) {
        // Add completed selection to active region group
        this.timeline.addRegionToActiveGroup(
          this.timeline.selection.start,
          this.timeline.selection.end
        );
        this.timeline.selection = null; // Clear temporary selection

        if (this.daw && this.daw.onRegionsChange) {
          this.daw.onRegionsChange(this.timeline.activeRegionGroupId);
        }
      }
      this.selectionDragState = null;
      this.canvas.style.cursor = "crosshair";
      this.scheduleRender();
      return;
    }

    if (!this.dragState) return;

    if (this.dragState.isDragging) {
      this.canvas.style.cursor = "crosshair";

      if (this.daw && this.daw.isPlaying) {
        this.restartTrackDuringPlayback(this.dragState.track);
      }

      // Recalculate duration (track may have been dragged to extend timeline)
      if (this.daw) {
        this.daw.recalculateDuration();
      }

      // Fire track offset change event
      if (this.daw && this.daw.onTrackOffsetChange) {
        this.daw.onTrackOffsetChange(
          this.dragState.track.id,
          this.dragState.track.timeOffset
        );
      }

      this.dragState = null;
      this.scheduleRender();
    } else {
      if (this.daw) {
        const seekTime = this.dragState.clickTime;
        this.daw.seek(seekTime);

        this.dragState = null;
        this.scheduleRender();
      } else {
        this.dragState = null;
      }
    }
  }

  handleDoubleClick(e) {
    // Double-click to clear selection
    if (this.timeline.selection) {
      this.timeline.selection = null;
      this.scheduleRender();
      if (this.daw && this.daw.onSelectionChange) {
        this.daw.onSelectionChange(null, null);
      }
    }
  }

  handleContextMenu(e) {
    e.preventDefault(); // Prevent default context menu

    // Check if right-clicking on a region (works anywhere on canvas now)
    const regionAtPoint = this.getRegionAtPointAnywhere(e.offsetX, e.offsetY);
    if (regionAtPoint) {
      const activeGroup = this.timeline.activeRegionGroupId
        ? this.timeline.regionGroups.get(this.timeline.activeRegionGroupId)
        : null;

      // Show context menu
      this.showRegionContextMenu(
        e.clientX,
        e.clientY,
        regionAtPoint,
        activeGroup
      );
    }
  }

  showRegionContextMenu(x, y, regionAtPoint, activeGroup) {
    // Remove any existing context menu
    this.hideContextMenu();

    // Create context menu
    const menu = document.createElement("div");
    menu.id = "daw-context-menu";
    menu.style.cssText = `
      position: fixed;
      left: ${x}px;
      top: ${y}px;
      background: ${this.theme.selectionMenu.background || "#2c2c2c"};
      border: 1px solid ${this.theme.selectionMenu.border || "#444"};
      border-radius: 4px;
      box-shadow: ${
        this.theme.selectionMenu.boxShadow || "0 4px 12px rgba(0, 0, 0, 0.5)"
      };
      padding: 4px 0;
      min-width: 180px;
      z-index: 10000;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif;
      font-size: ${this.theme.selectionMenu.fontSize || "13px"};
      color: ${this.theme.selectionMenu.color || "#fff"};
    `;

    // Add menu items
    const isReadOnly = activeGroup && activeGroup.readOnly;
    const region = regionAtPoint.region;

    // Remove option
    const removeItem = document.createElement("div");
    removeItem.textContent = "Remove Region";
    removeItem.style.cssText = `
      padding: 8px 16px;
      cursor: ${isReadOnly ? "not-allowed" : "pointer"};
      color: ${
        isReadOnly
          ? this.theme.selectionMenu.readOnlyTextColor || "#666"
          : this.theme.selectionMenu.textColor || "#fff"
      };
    `;

    if (!isReadOnly) {
      removeItem.addEventListener("mouseenter", () => {
        removeItem.style.background =
          this.theme.selectionMenu.mouseEnterBackground || "#404040";
      });
      removeItem.addEventListener("mouseleave", () => {
        removeItem.style.background =
          this.theme.selectionMenu.mouseLeaveBackground || "transparent";
      });
      removeItem.addEventListener("click", () => {
        this.timeline.removeRegionFromActiveGroup(regionAtPoint.index);
        this.scheduleRender();

        // Trigger onRegionsChange callback
        if (
          this.daw &&
          this.daw.onRegionsChange &&
          this.timeline.activeRegionGroupId
        ) {
          this.daw.onRegionsChange(this.timeline.activeRegionGroupId);
        }

        if (this.daw) {
          this.daw.log(`Removed region at index ${regionAtPoint.index}`);
        }
        this.hideContextMenu();
      });
    } else {
      // If read-only, still close menu when clicked
      removeItem.addEventListener("click", () => {
        this.hideContextMenu();
      });
    }

    menu.appendChild(removeItem);

    // Add separator
    const separator = document.createElement("div");
    separator.style.cssText = `
      height: 1px;
      background: ${this.theme.selectionMenu.separatorBackground || "#444"};
      margin: 4px 0;
    `;
    menu.appendChild(separator);

    // Add info item (non-clickable)
    const infoItem = document.createElement("div");
    const startTime = region.start.toFixed(2);
    const endTime = region.end.toFixed(2);
    const duration = (region.end - region.start).toFixed(2);
    infoItem.innerHTML = `
      <div style="padding: 4px 16px; color: ${
        this.theme.selectionMenu.detailsTextColor || "#999"
      }; font-size: ${this.theme.selectionMenu.detailsFontSize || "11px"};">
        Start: ${startTime}s<br>
        End: ${endTime}s<br>
        Duration: ${duration}s
      </div>
    `;
    menu.appendChild(infoItem);

    document.body.appendChild(menu);

    // Store reference for cleanup
    this.contextMenu = menu;

    // Close menu when clicking outside
    const closeHandler = (event) => {
      if (!menu.contains(event.target)) {
        this.hideContextMenu();
        document.removeEventListener("mousedown", closeHandler);
      }
    };
    setTimeout(() => {
      document.addEventListener("mousedown", closeHandler);
    }, 0);
  }

  hideContextMenu() {
    if (this.contextMenu) {
      this.contextMenu.remove();
      this.contextMenu = null;
    }
  }

  handleKeyDown(e) {
    // Escape key to clear selection
    if (e.key === "Escape" && this.timeline.selection) {
      this.timeline.selection = null;
      this.scheduleRender();
      if (this.daw && this.daw.onSelectionChange) {
        this.daw.onSelectionChange(null, null);
      }
    }
  }

  restartTrackDuringPlayback(track) {
    const oldSource = this.daw.audioSources.get(track.id);
    if (oldSource) {
      try {
        oldSource.stop();
      } catch (e) {
        // Ignore
      }
      this.daw.audioSources.delete(track.id);
    }

    const source = this.daw.audioContext.createBufferSource();
    source.buffer = track.audioBuffer;
    // Connect to the first node in the chain (preGainNode)
    source.connect(track.preGainNode);

    const currentTime = this.daw.currentTime;
    const timeOffset = track.timeOffset;

    const timePlayOffset = timeOffset - currentTime;
    const when = timePlayOffset > 0 ? timePlayOffset : 0;
    const offset = timePlayOffset > 0 ? 0 : -timePlayOffset;

    if (offset < track.audioBuffer.duration) {
      source.start(when + this.daw.audioContext.currentTime, offset);
      this.daw.audioSources.set(track.id, source);
    }
  }

  scheduleRender() {
    if (this.renderScheduled) return;
    this.renderScheduled = true;
    requestAnimationFrame(() => {
      this.render();
      this.renderScheduled = false;
    });
  }

  render() {
    const width = this.canvas.offsetWidth;
    const height = this.canvas.offsetHeight;

    this.ctx.clearRect(0, 0, width, height);

    this.drawBackground();
    this.drawGrid();
    this.drawTracks();
    this.drawTimelineRuler();
    this.drawHorizontalScrollbar();
    this.drawSelection();
    this.drawPlayhead();

    if (this.minimapCanvas) {
      this.renderMinimap();
    }
  }

  renderMinimap() {
    const width = this.minimapCanvas.offsetWidth;
    const height = this.minimapCanvas.offsetHeight;

    this.minimapCtx.clearRect(0, 0, width, height);

    this.minimapCtx.fillStyle = this.theme.minimapBackground;
    this.minimapCtx.fillRect(0, 0, width, height);

    const totalDuration = this.timeline.duration || 10;
    const pixelsPerSecond = width / totalDuration;

    this.timeline.tracks.forEach((track, index) => {
      if (!track.audioBuffer) return;

      const trackY = 5 + index * 20;
      const trackHeight = 15;

      const startX = track.timeOffset * pixelsPerSecond;
      const trackWidth = track.audioBuffer.duration * pixelsPerSecond;

      this.minimapCtx.fillStyle = this.theme.minimapTrackBackground;
      this.minimapCtx.fillRect(startX, trackY, trackWidth, trackHeight);

      this.minimapCtx.fillStyle =
        track.color || this.theme.defaultWaveformColor;
      this.minimapCtx.globalAlpha = this.theme.minimapWaveformOpacity;
      this.minimapCtx.fillRect(startX, trackY + 3, trackWidth, trackHeight - 6);
      this.minimapCtx.globalAlpha = 1.0;
    });

    const viewportStartX = this.timeline.viewportScrollTime * pixelsPerSecond;
    const viewportWidth = this.timeline.getVisibleDuration() * pixelsPerSecond;

    this.minimapCtx.fillStyle = this.theme.minimapViewportFill;
    this.minimapCtx.fillRect(viewportStartX, 0, viewportWidth, height);

    this.minimapCtx.strokeStyle = this.theme.minimapViewportStroke;
    this.minimapCtx.lineWidth = this.theme.minimapViewportStrokeWidth;
    this.minimapCtx.strokeRect(viewportStartX, 0, viewportWidth, height);

    // Draw regions in minimap
    const regions = this.timeline.getActiveRegions();
    for (const region of regions) {
      const regStartX = region.start * pixelsPerSecond;
      const regEndX = region.end * pixelsPerSecond;

      this.minimapCtx.fillStyle = this.theme.selectionFill;
      this.minimapCtx.fillRect(regStartX, 0, regEndX - regStartX, height);

      this.minimapCtx.strokeStyle = this.theme.selectionBorder;
      this.minimapCtx.lineWidth = 2;
      this.minimapCtx.strokeRect(regStartX, 0, regEndX - regStartX, height);
    }

    // Draw temporary selection in minimap (while dragging)
    if (this.timeline.selection) {
      const selStartX = this.timeline.selection.start * pixelsPerSecond;
      const selEndX = this.timeline.selection.end * pixelsPerSecond;

      this.minimapCtx.fillStyle = this.theme.selectionFill;
      this.minimapCtx.fillRect(selStartX, 0, selEndX - selStartX, height);

      this.minimapCtx.strokeStyle = this.theme.selectionBorder;
      this.minimapCtx.lineWidth = 2;
      this.minimapCtx.strokeRect(selStartX, 0, selEndX - selStartX, height);
    }

    if (this.daw) {
      const playheadX = this.daw.currentTime * pixelsPerSecond;
      this.minimapCtx.strokeStyle = this.theme.minimapPlayheadColor;
      this.minimapCtx.lineWidth = this.theme.minimapPlayheadWidth;
      this.minimapCtx.beginPath();
      this.minimapCtx.moveTo(playheadX, 0);
      this.minimapCtx.lineTo(playheadX, height);
      this.minimapCtx.stroke();
    }
  }

  handleMinimapMouseDown(e) {
    const width = this.minimapCanvas.offsetWidth;
    const totalDuration = this.timeline.duration || 10;
    const pixelsPerSecond = width / totalDuration;

    const clickTime = e.offsetX / pixelsPerSecond;
    const viewportWidth = this.timeline.getVisibleDuration();

    const viewportStartX = this.timeline.viewportScrollTime * pixelsPerSecond;
    const viewportEndX =
      (this.timeline.viewportScrollTime + viewportWidth) * pixelsPerSecond;

    if (e.offsetX >= viewportStartX && e.offsetX <= viewportEndX) {
      this.minimapDragState = {
        isDragging: true,
        startMouseX: e.offsetX,
        startScrollTime: this.timeline.viewportScrollTime,
      };
      this.minimapCanvas.style.cursor = "grabbing";
    } else {
      const newScrollTime = clickTime - viewportWidth / 2;
      this.scrollToTime(newScrollTime);
    }
  }

  handleMinimapMouseMove(e) {
    if (!this.minimapDragState || !this.minimapDragState.isDragging) {
      const width = this.minimapCanvas.offsetWidth;
      const totalDuration = this.timeline.duration || 10;
      const pixelsPerSecond = width / totalDuration;
      const viewportWidth = this.timeline.getVisibleDuration();
      const viewportStartX = this.timeline.viewportScrollTime * pixelsPerSecond;
      const viewportEndX =
        (this.timeline.viewportScrollTime + viewportWidth) * pixelsPerSecond;

      if (e.offsetX >= viewportStartX && e.offsetX <= viewportEndX) {
        this.minimapCanvas.style.cursor = "grab";
      } else {
        this.minimapCanvas.style.cursor = "pointer";
      }
      return;
    }

    const width = this.minimapCanvas.offsetWidth;
    const totalDuration = this.timeline.duration || 10;
    const pixelsPerSecond = width / totalDuration;

    const deltaX = e.offsetX - this.minimapDragState.startMouseX;
    const deltaTime = deltaX / pixelsPerSecond;

    const newScrollTime = this.minimapDragState.startScrollTime + deltaTime;
    this.scrollToTime(newScrollTime);
  }

  handleMinimapMouseUp(e) {
    if (this.minimapDragState) {
      this.minimapDragState = null;
      this.minimapCanvas.style.cursor = "pointer";
    }
  }

  scrollToTime(time) {
    const visibleDuration = this.timeline.getVisibleDuration();
    const effectiveDuration = this.timeline.getEffectiveDuration(
      this.canvas.offsetWidth
    );
    const maxScroll = Math.max(0, effectiveDuration - visibleDuration);

    this.timeline.viewportScrollTime = Math.max(0, Math.min(maxScroll, time));
    this.timeline.updateViewport(this.canvas.offsetWidth);
    this.scheduleRender();

    if (this.daw && this.daw.onScrollChange) {
      this.daw.onScrollChange(this.timeline.viewportScrollTime);
    }
  }

  drawBackground() {
    const width = this.canvas.offsetWidth;
    const height = this.canvas.offsetHeight;

    this.ctx.fillStyle = this.theme.background;
    this.ctx.fillRect(0, 0, width, height);
  }

  drawGrid() {
    const width = this.canvas.offsetWidth;
    const height = this.canvas.offsetHeight - this.rulerHeight;

    let gridInterval = 1;
    if (this.timeline.pixelsPerSecond < 20) {
      gridInterval = 5;
    } else if (this.timeline.pixelsPerSecond > 100) {
      gridInterval = 0.5;
    }

    this.ctx.strokeStyle = this.theme.gridColor;
    this.ctx.lineWidth = this.theme.gridLineWidth;

    for (
      let time =
        Math.floor(this.timeline.viewportStartTime / gridInterval) *
        gridInterval;
      time <= this.timeline.viewportEndTime;
      time += gridInterval
    ) {
      const x = this.timeline.timeToCanvasX(time);
      this.ctx.beginPath();
      this.ctx.moveTo(x, 0);
      this.ctx.lineTo(x, height);
      this.ctx.stroke();
    }
  }

  drawTracks() {
    const trackHeight = this.calculateTrackHeight();

    this.ctx.save();

    const viewportHeight = this.canvas.offsetHeight - this.rulerHeight;
    this.ctx.beginPath();
    this.ctx.rect(0, 0, this.canvas.offsetWidth, viewportHeight);
    this.ctx.clip();

    this.timeline.tracks.forEach((track, index) => {
      const trackY = 20 + index * (trackHeight + this.trackSpacing);
      const displayY = trackY - this.verticalScrollOffset;

      if (displayY + trackHeight >= 0 && displayY <= viewportHeight) {
        this.drawTrack(track, displayY, trackHeight);
      }
    });

    this.ctx.restore();
    this.drawVerticalScrollbar();
  }

  drawTrack(track, y, height) {
    if (!track.audioBuffer || !track.waveformData) {
      // Draw loading indicator if track is loading
      if (track.isLoading) {
        const width = this.canvas.offsetWidth;
        this.ctx.fillStyle = this.theme.trackBackground;
        this.ctx.fillRect(0, y, width, height);

        this.ctx.fillStyle = this.theme.loadingTextColor;
        this.ctx.font = this.theme.loadingTextFont;
        this.ctx.textAlign = "center";

        // Show specific loading phase
        let loadingText = "Loading...";
        if (track.loadingPhase === "audio") {
          loadingText = "Loading audio...";
        } else if (track.loadingPhase === "waveform") {
          loadingText = "Generating waveform...";
        }

        this.ctx.fillText(loadingText, width / 2, y + height / 2);
        this.ctx.textAlign = "left";
      }
      return;
    }

    const width = this.canvas.offsetWidth;

    const trackStartTime = track.timeOffset;
    const trackEndTime = track.timeOffset + track.audioBuffer.duration;

    const trackStartX = this.timeline.timeToCanvasX(trackStartTime);
    const trackEndX = this.timeline.timeToCanvasX(trackEndTime);

    const bgStartX = Math.max(0, trackStartX);
    const bgEndX = Math.min(width, trackEndX);

    if (bgEndX > bgStartX) {
      const isBeingDragged =
        this.dragState &&
        this.dragState.isDragging &&
        this.dragState.track === track;

      this.ctx.fillStyle = isBeingDragged
        ? this.theme.trackBackgroundDragging
        : this.theme.trackBackground;
      this.ctx.fillRect(bgStartX, y, bgEndX - bgStartX, height);

      if (isBeingDragged) {
        this.ctx.strokeStyle = this.theme.trackBorderColor;
        this.ctx.lineWidth = this.theme.trackBorderWidth;
        this.ctx.strokeRect(bgStartX, y, bgEndX - bgStartX, height);
      }
    }

    const startTime = Math.max(
      0,
      this.timeline.viewportStartTime - track.timeOffset
    );
    const endTime = this.timeline.viewportEndTime - track.timeOffset;

    if (endTime < 0 || startTime > track.audioBuffer.duration) return;

    const pixelsPerSample =
      this.timeline.pixelsPerSecond / track.audioBuffer.sampleRate;
    const resolution =
      WaveformGenerator.selectWaveformResolution(pixelsPerSample);
    const peaks = track.waveformData[resolution];

    // Log mipmap level changes
    const lastResolution = this.lastMipmapLevels.get(track.id);
    if (lastResolution !== resolution) {
      if (this.daw) {
        this.daw.log(
          `[Mipmap] Track "${track.fileName}" (${track.id}) switched from 1/${
            lastResolution || "none"
          } to 1/${resolution} (pixelsPerSample: ${pixelsPerSample.toFixed(
            6
          )}, zoom: ${this.timeline.pixelsPerSecond.toFixed(1)}px/s)`
        );
      }
      this.lastMipmapLevels.set(track.id, resolution);
    }

    if (!peaks) return;

    const startPeak = Math.max(
      0,
      Math.floor((startTime * track.audioBuffer.sampleRate) / resolution)
    );
    const endPeak = Math.min(
      peaks.length,
      Math.ceil((endTime * track.audioBuffer.sampleRate) / resolution)
    );

    this.ctx.strokeStyle = track.color || this.theme.defaultWaveformColor;
    this.ctx.lineWidth = this.theme.waveformLineWidth;
    this.ctx.beginPath();

    // Calculate gain multiplier once (if waveform gain scaling is enabled)
    const gainMultiplier =
      this.theme.waveformGainScaling && track.gain !== 0
        ? Math.pow(10, track.gain / 20)
        : 1.0;

    for (let i = startPeak; i < endPeak; i++) {
      const peakTime = (i * resolution) / track.audioBuffer.sampleRate;
      const x = this.timeline.timeToCanvasX(peakTime + track.timeOffset);

      if (x < 0 || x > width) continue;

      // Apply gain scaling with soft clipping using tanh
      let peakMax = peaks[i].max;
      let peakMin = peaks[i].min;

      if (this.theme.waveformGainScaling && gainMultiplier !== 1.0) {
        peakMax = Math.tanh(peakMax * gainMultiplier);
        peakMin = Math.tanh(peakMin * gainMultiplier);
      }

      const y1 = y + height * (1 - (peakMax + 1) / 2);
      const y2 = y + height * (1 - (peakMin + 1) / 2);

      this.ctx.moveTo(x, y1);
      this.ctx.lineTo(x, y2);
    }

    this.ctx.stroke();

    if (track.fileName) {
      this.ctx.fillStyle = this.theme.trackLabelColor;
      this.ctx.font = this.theme.trackLabelFont;
      this.ctx.textAlign = "left";
      this.ctx.fillText(track.fileName, track.fileName.length + 2, y + 20);
    }

    const isBeingDragged =
      this.dragState &&
      this.dragState.isDragging &&
      this.dragState.track === track;
    if (isBeingDragged) {
      const offsetText = `Offset: ${track.timeOffset.toFixed(3)}s`;
      this.ctx.fillStyle = this.theme.offsetDisplayBackground;
      this.ctx.fillRect(10, y + 40, 140, 24);
      this.ctx.fillStyle = this.theme.offsetDisplayColor;
      this.ctx.font = this.theme.offsetDisplayFont;
      this.ctx.fillText(offsetText, 15, y + 56);
    }
  }

  drawTimelineRuler() {
    const width = this.canvas.offsetWidth;
    const height = this.canvas.offsetHeight;
    const rulerY = height - this.rulerHeight;

    this.ctx.fillStyle = this.theme.rulerBackground;
    this.ctx.fillRect(0, rulerY, width, this.rulerHeight);

    // Dynamically calculate tick interval to keep labels readable
    // Target: 60-120 pixels between major ticks
    const targetPixelsBetweenTicks = 80;
    const secondsBetweenTicks =
      targetPixelsBetweenTicks / this.timeline.pixelsPerSecond;

    // Round to nice intervals: 0.1, 0.5, 1, 5, 10, 30, 60, 300, 600, 1800, 3600, etc.
    const niceIntervals = [
      0.1, 0.5, 1, 5, 10, 30, 60, 300, 600, 1800, 3600, 7200, 18000, 36000,
    ];
    let majorTickInterval = niceIntervals[0];

    for (let i = 0; i < niceIntervals.length; i++) {
      if (niceIntervals[i] >= secondsBetweenTicks) {
        majorTickInterval = niceIntervals[i];
        break;
      }
      if (i === niceIntervals.length - 1) {
        majorTickInterval = niceIntervals[i];
      }
    }

    const minorTickInterval = majorTickInterval / 4;

    this.ctx.strokeStyle = this.theme.rulerTickColor;
    this.ctx.fillStyle = this.theme.rulerTextColor;
    this.ctx.font = this.theme.rulerTextFont;
    this.ctx.textAlign = "center";

    for (
      let time =
        Math.floor(this.timeline.viewportStartTime / majorTickInterval) *
        majorTickInterval;
      time <= this.timeline.viewportEndTime;
      time += minorTickInterval
    ) {
      const x = this.timeline.timeToCanvasX(time);
      const isMajor = Math.abs(time % majorTickInterval) < 0.001;

      // Calculate tick start position - with configurable bottom margin
      const tickStartY = height - (this.theme.rulerTickBottomMargin || 0);
      const tickHeight = isMajor
        ? this.theme.rulerMajorTickHeight
        : this.theme.rulerMinorTickHeight;
      const tickEndY = rulerY + this.rulerHeight - tickHeight;

      this.ctx.beginPath();
      this.ctx.moveTo(x, tickStartY);
      this.ctx.lineTo(x, tickEndY);
      this.ctx.stroke();

      if (isMajor) {
        const label = this.formatTime(time);
        this.ctx.fillText(label, x, rulerY + 12);
      }
    }
  }

  shouldShowVerticalScrollbar() {
    return this.theme.scrollbar.showVertical && this.getMaxVerticalScroll() > 0;
  }

  shouldShowHorizontalScrollbar() {
    return this.theme.scrollbar.showHorizontal;
  }

  getVerticalScrollbarGeometry() {
    const scrollbarWidth = this.theme.scrollbar.width;
    const scrollbarX = this.canvas.offsetWidth - scrollbarWidth - 4;
    const scrollbarY = 4;

    // Subtract ruler and horizontal scrollbar space from available height
    const horizontalScrollbarSpace = this.shouldShowHorizontalScrollbar()
      ? this.theme.scrollbar.width + 8
      : 0;
    const scrollbarHeight =
      this.canvas.offsetHeight -
      this.rulerHeight -
      horizontalScrollbarSpace -
      8;

    const maxScroll = this.getMaxVerticalScroll();
    const contentHeight = this.getTotalContentHeight();
    const viewportHeight = this.canvas.offsetHeight - this.rulerHeight;
    const thumbHeight = Math.max(
      this.theme.scrollbar.minThumbSize,
      (viewportHeight / contentHeight) * scrollbarHeight
    );
    const scrollRatio =
      maxScroll > 0 ? this.verticalScrollOffset / maxScroll : 0;
    const thumbY = scrollbarY + (scrollbarHeight - thumbHeight) * scrollRatio;

    return {
      scrollbarX,
      scrollbarY,
      scrollbarWidth,
      scrollbarHeight,
      thumbY,
      thumbHeight,
      maxScroll,
    };
  }

  getHorizontalScrollbarGeometry() {
    const scrollbarHeight = this.theme.scrollbar.width; // Using width as height (thickness)
    const scrollbarY = this.canvas.offsetHeight - scrollbarHeight - 4; // Below ruler
    const scrollbarX = 4;

    // Account for vertical scrollbar space on the right
    const verticalScrollbarSpace = this.shouldShowVerticalScrollbar()
      ? this.theme.scrollbar.width + 8
      : 0;
    const scrollbarWidth = this.canvas.offsetWidth - 8 - verticalScrollbarSpace;

    // Calculate thumb size and position based on timeline viewport
    const totalDuration = this.timeline.getEffectiveDuration(
      this.canvas.offsetWidth
    );
    const visibleDuration = this.timeline.getVisibleDuration();
    const thumbWidth = Math.max(
      this.theme.scrollbar.minThumbSize,
      (visibleDuration / totalDuration) * scrollbarWidth
    );

    const maxScroll = Math.max(0, totalDuration - visibleDuration);
    const scrollRatio =
      maxScroll > 0 ? this.timeline.viewportScrollTime / maxScroll : 0;
    const thumbX = scrollbarX + (scrollbarWidth - thumbWidth) * scrollRatio;

    return {
      scrollbarX,
      scrollbarY,
      scrollbarWidth,
      scrollbarHeight,
      thumbX,
      thumbWidth,
      maxScroll,
    };
  }

  isPointOnVerticalScrollbarThumb(x, y) {
    if (!this.shouldShowVerticalScrollbar()) return false;
    const geom = this.getVerticalScrollbarGeometry();

    return (
      x >= geom.scrollbarX &&
      x <= geom.scrollbarX + geom.scrollbarWidth &&
      y >= geom.thumbY &&
      y <= geom.thumbY + geom.thumbHeight
    );
  }

  isPointOnVerticalScrollbarTrack(x, y) {
    if (!this.shouldShowVerticalScrollbar()) return false;
    const geom = this.getVerticalScrollbarGeometry();

    const viewportHeight = this.canvas.offsetHeight - this.rulerHeight;
    return (
      x >= geom.scrollbarX &&
      x <= geom.scrollbarX + geom.scrollbarWidth &&
      y >= 0 &&
      y <= viewportHeight
    );
  }

  isPointOnHorizontalScrollbarThumb(x, y) {
    if (!this.shouldShowHorizontalScrollbar()) return false;
    const geom = this.getHorizontalScrollbarGeometry();

    return (
      x >= geom.thumbX &&
      x <= geom.thumbX + geom.thumbWidth &&
      y >= geom.scrollbarY &&
      y <= geom.scrollbarY + geom.scrollbarHeight
    );
  }

  isPointOnHorizontalScrollbarTrack(x, y) {
    if (!this.shouldShowHorizontalScrollbar()) return false;
    const geom = this.getHorizontalScrollbarGeometry();

    return (
      x >= geom.scrollbarX &&
      x <= geom.scrollbarX + geom.scrollbarWidth &&
      y >= geom.scrollbarY &&
      y <= geom.scrollbarY + geom.scrollbarHeight
    );
  }

  drawDefaultScrollbar(geom, isActive, orientation) {
    // Draw track background
    this.ctx.fillStyle = this.theme.scrollbar.trackColor;
    this.ctx.fillRect(
      geom.scrollbarX,
      geom.scrollbarY,
      geom.scrollbarWidth,
      geom.scrollbarHeight
    );

    // Draw thumb
    this.ctx.fillStyle = isActive
      ? this.theme.scrollbar.thumbColorActive
      : this.theme.scrollbar.thumbColor;
    if (orientation === "vertical") {
      this.ctx.fillRect(
        geom.scrollbarX,
        geom.thumbY,
        geom.scrollbarWidth,
        geom.thumbHeight
      );
    } else {
      this.ctx.fillRect(
        geom.thumbX,
        geom.scrollbarY,
        geom.thumbWidth,
        geom.scrollbarHeight
      );
    }

    // Draw thumb border
    this.ctx.strokeStyle = this.theme.scrollbar.thumbBorder;
    this.ctx.lineWidth = 1;
    if (orientation === "vertical") {
      this.ctx.strokeRect(
        geom.scrollbarX,
        geom.thumbY,
        geom.scrollbarWidth,
        geom.thumbHeight
      );
    } else {
      this.ctx.strokeRect(
        geom.thumbX,
        geom.scrollbarY,
        geom.thumbWidth,
        geom.scrollbarHeight
      );
    }
  }

  drawVerticalScrollbar() {
    if (!this.shouldShowVerticalScrollbar()) return;

    const geom = this.getVerticalScrollbarGeometry();
    const isActive = this.scrollbarDragState !== null;

    if (this.theme.scrollbar.customDrawFunction) {
      this.theme.scrollbar.customDrawFunction(
        this.ctx,
        geom,
        isActive,
        this.theme,
        "vertical"
      );
    } else {
      this.drawDefaultScrollbar(geom, isActive, "vertical");
    }
  }

  drawHorizontalScrollbar() {
    if (!this.shouldShowHorizontalScrollbar()) return;

    const geom = this.getHorizontalScrollbarGeometry();
    const isActive = this.horizontalScrollbarDragState !== null;

    if (this.theme.scrollbar.customDrawFunction) {
      this.theme.scrollbar.customDrawFunction(
        this.ctx,
        geom,
        isActive,
        this.theme,
        "horizontal"
      );
    } else {
      this.drawDefaultScrollbar(geom, isActive, "horizontal");
    }
  }

  drawSelection() {
    const width = this.canvas.offsetWidth;
    const height = this.canvas.offsetHeight;
    const rulerY = height - this.rulerHeight;

    // Draw all regions from active group
    const regions = this.timeline.getActiveRegions();
    for (let i = 0; i < regions.length; i++) {
      const region = regions[i];
      // Show labels if: hovering, dragging this region, or creating (temporary selection)
      const showLabels =
        this.hoveredRegionIndex === i ||
        (this.regionDragState && this.regionDragState.regionIndex === i);
      this.drawRegion(region, width, height, rulerY, showLabels);
    }

    // Draw temporary selection (while dragging to create new region)
    if (this.timeline.selection) {
      this.drawRegion(this.timeline.selection, width, height, rulerY, true);
    }
  }

  drawRegion(region, _width, height, rulerY, showLabels) {
    const startX = this.timeline.timeToCanvasX(region.start);
    const endX = this.timeline.timeToCanvasX(region.end);

    // Draw selection fill over entire height
    this.ctx.fillStyle = this.theme.selectionFill;
    this.ctx.fillRect(startX, 0, endX - startX, height);

    // Draw selection borders
    this.ctx.strokeStyle = this.theme.selectionBorder;
    this.ctx.lineWidth = this.theme.selectionBorderWidth;
    this.ctx.beginPath();
    this.ctx.moveTo(startX, 0);
    this.ctx.lineTo(startX, height);
    this.ctx.moveTo(endX, 0);
    this.ctx.lineTo(endX, height);
    this.ctx.stroke();

    // Draw handles in ruler area for all regions
    const handleWidth = this.theme.selectionHandleWidth;
    const handleHeight = 30; // Fixed 30px height
    const handleY = rulerY + 5; // 5px margin from top of ruler (leaves 25px at bottom)

    this.ctx.fillStyle = this.theme.selectionHandleColor;
    this.ctx.fillRect(
      startX - handleWidth / 2,
      handleY,
      handleWidth,
      handleHeight
    );
    this.ctx.fillRect(
      endX - handleWidth / 2,
      handleY,
      handleWidth,
      handleHeight
    );

    // Draw time labels when hovering, dragging, or creating
    if (showLabels) {
      this.ctx.fillStyle = this.theme.selectionBorder;
      this.ctx.font = "10px monospace";
      this.ctx.textAlign = "center";

      const startLabel = region.start.toFixed(3) + "s";
      const endLabel = region.end.toFixed(3) + "s";
      const durationLabel = (region.end - region.start).toFixed(3) + "s";

      this.ctx.fillText(
        startLabel,
        startX - startLabel.length - 20,
        rulerY + 25
      );
      this.ctx.fillText(endLabel, endX + endLabel.length + 20, rulerY + 25);
      this.ctx.fillText(durationLabel, (startX + endX) / 2, rulerY - 6);
    }

    this.ctx.lineWidth = 1;
  }

  drawPlayhead() {
    const currentTime = this.daw ? this.daw.currentTime : 0;
    const x = this.timeline.timeToCanvasX(currentTime);

    if (x >= 0 && x <= this.canvas.offsetWidth) {
      this.ctx.strokeStyle = this.theme.playheadColor;
      this.ctx.lineWidth = this.theme.playheadWidth;
      this.ctx.beginPath();
      this.ctx.moveTo(x, 0);
      this.ctx.lineTo(x, this.canvas.offsetHeight);
      this.ctx.stroke();
      this.ctx.lineWidth = 1;
    }
  }

  formatTime(seconds) {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      // Format: 1:23:45 or 12:34:56
      return `${hours}:${mins.toString().padStart(2, "0")}:${Math.floor(secs)
        .toString()
        .padStart(2, "0")}`;
    } else if (mins > 0) {
      // Format: 1:23.4 or 12:34.5
      return `${mins}:${secs.toFixed(1).padStart(4, "0")}`;
    } else {
      // Format: 12.3s
      return `${secs.toFixed(1)}s`;
    }
  }

  log(message) {
    if (this.daw && this.daw.log) {
      this.daw.log(message);
    }
  }
}
